import React, { FC } from 'react';
import { Modal, ModalBody, ModalClose, Box, Text, ButtonAppearance, Icon, IconSize, theme } from '@bechtel/gds';
import { DrawerFooterButtonStyled, useEnvProvider, ProjectAppTypes } from '@bechtel/client-common';
import {
  RemoveDisciplinesModelBlockStyled,
  RemoveDisciplinesModelActionBlockStyled,
  DisciplinesItemContainerStyled,
  DisciplinesItemStyled,
  ModalHeaderStyled,
  ModalFooterStyled,
} from './styled';
import { WorkPackageDiscipline } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';

interface RemoveDisciplinesModalProps {
  onCancel: () => void;
  onConfirm: () => void;
  disciplinesToRemove: WorkPackageDiscipline[];
}

const RemoveDisciplinesModal: FC<RemoveDisciplinesModalProps> = ({ onCancel, onConfirm, disciplinesToRemove }) => {
  const { envConfig } = useEnvProvider();
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  return (
    <Modal visible={true} style={{ width: '552px', height: '510px' }}>
      <ModalHeaderStyled title={t`Remove Disciplines`}>
        <ModalClose onClick={onCancel}></ModalClose>
      </ModalHeaderStyled>
      <ModalBody>
        <Box
          flexDirection={{ sm: 'column' }}
          justifyContent={{ sm: 'flex-start' }}
          alignItems={{ sm: 'flex-start' }}
          flexGrow={{ sm: '1' }}
        >
          {disciplinesToRemove && (
            <RemoveDisciplinesModelBlockStyled>
              <Icon icon="exclamation-triangle" size={IconSize.LARGE} />
              <Text
                style={{ marginLeft: `${theme.spacings[450]}` }}
                size="500"
                family="graphik"
                color="grey.1000"
                weight="600"
              >
                <Trans>You are about to remove</Trans> {disciplinesToRemove.length}{' '}
                {disciplinesToRemove.length > 1 ? t`disciplines` : t`discipline`}.
              </Text>
            </RemoveDisciplinesModelBlockStyled>
          )}
          <Box
            flexDirection={{ sm: 'column' }}
            css={`
              overflow-y: auto;
            `}
          >
            <RemoveDisciplinesModelActionBlockStyled flexDirection={{ sm: 'column' }}>
              <Text
                size="400"
                weight="600"
                family="graphik"
                color="grey.1000"
                style={{ marginBottom: `${theme.spacings[500]}` }}
              >
                <Trans>This action cannot be undone.</Trans>
              </Text>
              <Text size="400" family="graphik" color="grey.1000" style={{ lineHeight: `${theme.spacings[550]}` }}>
                {t`All Work Packages and associated plans, blocks, and notifications will be removed. This scope will no
                  longer be managed or visible in ${isPFAppType ? 'PipeFighter' : 'ArcStryker'}.`}
                <br />
                <Trans>Are you sure you want to do this?</Trans>
              </Text>
            </RemoveDisciplinesModelActionBlockStyled>
            <DisciplinesItemContainerStyled>
              {disciplinesToRemove &&
                disciplinesToRemove.map((item) => {
                  return (
                    <DisciplinesItemStyled key={item.id}>
                      <Text size="400" color="grey.600">
                        {item.name}
                      </Text>
                    </DisciplinesItemStyled>
                  );
                })}
            </DisciplinesItemContainerStyled>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooterStyled>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <DrawerFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onCancel}>
              <Box px={{ sm: '500' }}>
                <Trans>Cancel</Trans>
              </Box>
            </DrawerFooterButtonStyled>
          </Box>
          <Box>
            <DrawerFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onConfirm}>
              <Box px={{ sm: '500' }}>
                <Trans>Remove Disciplines</Trans>
              </Box>
            </DrawerFooterButtonStyled>
          </Box>
        </Box>
      </ModalFooterStyled>
    </Modal>
  );
};

export default RemoveDisciplinesModal;
